/**
 * @file: index.tsx
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Leads feature flow (top-level, reached from the sidebar/drawer). A list of
 * leads → a lead management page (timeline, outreach logging, transitions) plus
 * the outreach-log and book-reservation forms.
 */

import { AppNavigationProvider } from '@euler/app/components/AppNavigationProvider';
import { useStatusBarStyle } from '@euler/app/components/AppStatusBar';
import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { LeadCenterProvider } from '@euler/app/flows/leads/LeadCenterContext';
import { CreateManualLeadScreen } from '@euler/app/flows/leads/pages/create-manual-lead';
import { CreateLeadReservationScreen } from '@euler/app/flows/leads/pages/create-reservation';
import { LeadDetailScreen } from '@euler/app/flows/leads/pages/detail';
import { LeadsListScreen } from '@euler/app/flows/leads/pages/list';
import { LogLeadActivityScreen } from '@euler/app/flows/leads/pages/log-activity';
import { AddVehicleInfoScreen } from '@euler/app/flows/order/pages/AddVehicleInfoScreen';
import { ServiceAgentSelectorScreen } from '@euler/app/flows/order/pages/ServiceAgentSelectorScreen';
import { VehicleBrandSelectorScreen } from '@euler/app/flows/order/pages/VehicleBrandSelectorScreen';
import { VehicleModelSelectorScreen } from '@euler/app/flows/order/pages/VehicleModelSelectorScreen';
import { ReservationDetailScreen } from '@euler/app/flows/reservations/pages/detail';
import { ReservationItemPickerScreen } from '@euler/app/flows/reservations/pages/pick-items';
import { ReservationCenterProvider } from '@euler/app/flows/reservations/provider';
import { MultilineHeaderTitle } from '@euler/components/MultilineHeaderTitle';
import { ErrorFallback } from '@euler/components/error';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsDesktopLayout } from '@euler/functions/responsiveness';
import { ScreenToParams, spreadScreens } from '@euler/functions/spreadScreens';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ScopedObservableSuspenseResourceContextProvider } from '@euler/utils/hooks';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import {
  TransitionPresets,
  createStackNavigator,
} from '@react-navigation/stack';
import * as Sentry from '@sentry/react';
import { memo } from 'react';

const Stack = createStackNavigator();

const Screens = {
  _main: LeadsListScreen,
  detail: LeadDetailScreen,
  logActivity: LogLeadActivityScreen,
  createReservation: CreateLeadReservationScreen,
  _createLead: CreateManualLeadScreen,
  _serviceAgentSelector: ServiceAgentSelectorScreen,
  // Borrowed from the order flow so the manual-lead form's vehicle picker works
  // inside this stack (brand → model → add-custom chain).
  _vehicleBrandSelector: VehicleBrandSelectorScreen,
  _vehicleModelSelector: VehicleModelSelectorScreen,
  _addVehicleInfo: AddVehicleInfoScreen,
  // A private (`_`, deep-link-ignored) embedding of the reservation detail so a
  // lead's "view reservation" link opens it within this stack and can navigate
  // back. The detail's item picker is embedded too, since it navigates within
  // this stack.
  _reservation: ReservationDetailScreen,
  _pickReservationItems: ReservationItemPickerScreen,
};

export type LeadsNavParams = ScreenToParams<typeof Screens>;

export const LeadsFlow = wrapNavigatorScreen(
  () => {
    useStatusBarStyle('dark');
    return <Content />;
  },
  {
    title: '商机',
    headerShown: false,
  },
);

/**
 * App-level (`_lead`) embedding of the lead detail: the same leads stack, but
 * opened straight at `detail`. Lets a feed thread push the lead detail onto the
 * ROOT stack — back returns to the feed thread (not the lead center) — while
 * its own sub-navigations (log activity, book reservation, ...) still resolve
 * because the whole stack + providers ride along.
 */
export const LeadDetailFlow = wrapNavigatorScreen(
  ({ id }: { id: string }) => {
    useStatusBarStyle('dark');
    return <Content initialRoute="detail" initialParams={{ id }} />;
  },
  {
    title: '商机',
    headerShown: false,
  },
);

const Content = memo(
  ({
    initialRoute = '_main',
    initialParams = {},
  }: {
    initialRoute?: string;
    initialParams?: Record<string, unknown>;
  }) => {
    const isDesktop = useIsDesktopLayout();
    const { rpx } = useResponsiveContext();
    return (
      <AppNavigationProvider>
        <Sentry.ErrorBoundary
          // @ts-ignore
          fallback={(params: any) => <ErrorFallback {...params} />}
        >
          <ScopedObservableSuspenseResourceContextProvider scope={'leads'}>
            <LeadCenterProvider>
              <ReservationCenterProvider>
                <BottomSheetModalProvider>
                  <Stack.Navigator
                    initialRouteName={initialRoute}
                    screenOptions={{
                      ...TransitionPresets.SlideFromRightIOS,
                      headerMode: 'float',
                      headerTitleAlign: isDesktop ? 'left' : 'center',
                      headerBackTitle: ' ',
                      headerTitleStyle: {
                        fontSize: rpx(isDesktop ? 20 : 18),
                        fontFamily: FontFamily.NotoSans.Bold,
                        color: '#0f172a',
                      },
                      headerStyle: {
                        backgroundColor: '#fff',
                        borderBottomWidth: 1,
                        borderBottomColor: '#e5e7eb',
                        shadowOpacity: 0,
                        elevation: 0,
                      },
                      cardStyle: { backgroundColor: '#f1f5f9' },
                      headerTitle: MultilineHeaderTitle,
                      headerBackImage: () => <BackIcon fill={'#0f172a'} />,
                    }}
                  >
                    {spreadScreens(
                      Stack.Screen,
                      Screens,
                      initialRoute,
                      initialParams,
                    )}
                  </Stack.Navigator>
                </BottomSheetModalProvider>
              </ReservationCenterProvider>
            </LeadCenterProvider>
          </ScopedObservableSuspenseResourceContextProvider>
        </Sentry.ErrorBoundary>
      </AppNavigationProvider>
    );
  },
);
